import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  criarNovoPaciente,
  getConfig,
  getCriancaAtiva,
  salvarConfig,
  salvarPaciente,
} from "../db/queries";
import type { SistemaCAA } from "../types";
import { idade } from "../lib/format";

/**
 * Cadastro do paciente e do responsável — dados básicos, só no aparelho (sem conta,
 * sem nuvem). Usado no primeiro uso (onboarding) e para editar depois.
 */
const VAZIO = {
  nome_completo: "",
  apelido: "",
  data_nascimento: "",
  sistema_caa: "nenhum" as SistemaCAA,
  responsavel_nome: "",
  responsavel_cpf: "",
  recomendacoes: "",
};

export function CadastroPaciente() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  // ?novo=1 → cria um NOVO paciente (fluxo do profissional), não edita o ativo
  const novo = params.get("novo") === "1";
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const perfilProf = Boolean(cfg?.perfil_profissional);
  const [form, setForm] = useState(VAZIO);
  const [carregado, setCarregado] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // prefill uma vez, quando o paciente ativo carrega — só ao EDITAR (não em "novo")
  useEffect(() => {
    if (!novo && crianca && !carregado) {
      setForm({
        nome_completo: crianca.nome_completo ?? "",
        apelido: crianca.apelido ?? "",
        data_nascimento: crianca.data_nascimento ?? "",
        sistema_caa: crianca.sistema_caa,
        responsavel_nome: crianca.responsavel_nome ?? "",
        responsavel_cpf: crianca.responsavel_cpf ?? "",
        recomendacoes: crianca.recomendacoes ?? "",
      });
      setCarregado(true);
    }
  }, [novo, crianca, carregado]);

  const set = (k: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const podeSalvar = form.nome_completo.trim() !== "" || form.apelido.trim() !== "";
  const anos = idade(form.data_nascimento || null);

  async function salvar() {
    if (!podeSalvar || salvando) return;
    setSalvando(true);
    const limpo = (s: string) => (s.trim() ? s.trim() : null);
    const apelido =
      form.apelido.trim() || form.nome_completo.trim().split(/\s+/)[0] || "paciente";
    const dados = {
      apelido,
      nome_completo: limpo(form.nome_completo),
      data_nascimento: form.data_nascimento || null,
      sistema_caa: form.sistema_caa,
      responsavel_nome: limpo(form.responsavel_nome),
      responsavel_cpf: limpo(form.responsavel_cpf),
      recomendacoes: limpo(form.recomendacoes),
    };
    // "novo" cria um paciente novo (não sobrescreve o ativo); senão edita o ativo
    if (novo) await criarNovoPaciente(dados);
    else await salvarPaciente(dados);
    nav(-1);
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">{novo ? "novo paciente" : "cadastro do paciente"}</h1>
      <p className="tela-sub">
        {novo ? "cria um novo paciente neste aparelho." : "fica só neste aparelho, sem conta."}
      </p>

      <section className="secao">
        <h2 className="secao-titulo">paciente</h2>
        <div className="card">
          <div className="campo">
            <label>nome completo</label>
            <input
              value={form.nome_completo}
              onChange={set("nome_completo")}
              placeholder="ex.: Téo Almeida Souza"
              autoFocus
            />
          </div>
          <div className="linha-campos">
            <div className="campo">
              <label>como você chama (apelido)</label>
              <input
                value={form.apelido}
                onChange={set("apelido")}
                placeholder="ex.: Téo"
              />
            </div>
            <div className="campo">
              <label>data de nascimento</label>
              <input
                type="date"
                value={form.data_nascimento}
                onChange={set("data_nascimento")}
              />
            </div>
          </div>
          {anos !== null && (
            <p className="meta" style={{ color: "var(--text-muted)", marginTop: "-6px" }}>
              {anos} {anos === 1 ? "ano" : "anos"}
            </p>
          )}
          <div className="campo" style={{ margin: 0 }}>
            <label>sistema de comunicação (CAA)</label>
            <select value={form.sistema_caa} onChange={set("sistema_caa")}>
              <option value="nenhum">nenhum</option>
              <option value="PECS">PECS</option>
              <option value="outro">outro</option>
            </select>
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">responsável</h2>
        <div className="card">
          <div className="campo">
            <label>nome completo</label>
            <input
              value={form.responsavel_nome}
              onChange={set("responsavel_nome")}
              placeholder="ex.: Mariana Almeida Souza"
            />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>CPF</label>
            <input
              value={form.responsavel_cpf}
              onChange={set("responsavel_cpf")}
              inputMode="numeric"
              placeholder="000.000.000-00"
            />
          </div>
        </div>
      </section>

      {/* Recomendações de preparo / embalagem: desativado no cadastro do paciente.
          Vai entrar em outra sessão/cadastro próprio. A lógica de dados (form.recomendacoes,
          prefill e salvarPaciente) fica preservada para não apagar valores já gravados.
      <section className="secao">
        <h2 className="secao-titulo">recomendações de preparo / embalagem</h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>como a comida deve vir (vai junto no pedido)</label>
            <textarea
              value={form.recomendacoes}
              onChange={set("recomendacoes")}
              placeholder="ex.: embalagem com divisórias; feijão sempre separado em espaço próprio. sem problema com custo extra de embalagem."
            />
          </div>
        </div>
      </section>
      */}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={!podeSalvar || salvando}
        onClick={salvar}
      >
        {novo ? "criar paciente" : "salvar cadastro"}
      </button>

      {/* Perfil profissional (flag local) — só ao editar "Meu Perfil", não em "novo". */}
      {!novo && (
        <section className="secao" style={{ marginTop: "var(--s6)" }}>
          <h2 className="secao-titulo">perfil</h2>
          <div className="card">
            <button
              className={`btn btn-bloco ${perfilProf ? "btn-primario" : ""}`}
              onClick={() => salvarConfig({ perfil_profissional: !perfilProf })}
            >
              {perfilProf ? "✓ perfil profissional ativo" : "sou profissional / clínica"}
            </button>
            <p className="meta" style={{ color: "var(--text-suave)", margin: "var(--s2) 0 0" }}>
              {perfilProf
                ? "o painel do profissional e a gestão de vários pacientes estão liberados. toque para desativar."
                : "ative para liberar o painel do profissional, a gestão de vários pacientes e a agenda da clínica."}
            </p>
          </div>
        </section>
      )}

      {/* Camada opcional (profissionais): agenda da clínica — só com perfil profissional. */}
      {!novo && perfilProf && (
        <section className="secao">
          <h2 className="secao-titulo">para profissionais</h2>
          <button
            className="card linha-alimento"
            style={{ width: "100%" }}
            onClick={() => nav("/agenda/config")}
          >
            <div className="foto" aria-hidden>🗓️</div>
            <div className="crescer">
              <div className="nome">agenda da clínica</div>
              <div className="meta">calendário de consultas — opcional, separado da terapia</div>
            </div>
            <span aria-hidden>→</span>
          </button>
        </section>
      )}
    </main>
  );
}
